<template>
  <v-card class="card-center pb-2" outlined>
    <v-container class="px-4 py-4" fluid>
      <v-card flat>
        <v-card-title class="py-0">
          {{ $t('ELE0028') }}
        </v-card-title>
        <v-card-text class="px-0 py-0">
          <validation-observer v-slot="{ invalid }">
            <v-row>
              <v-col cols="3">
                <TextFieldInput :label="$t('ELE0184')" v-model="searchCondition.saNo" />
              </v-col>
              <v-col cols="6">
                <SelectMultipleInput
                  :items="statusCdItems"
                  itemText="name"
                  itemValue="value"
                  :label="$t('ELE0218')"
                  v-model="searchCondition.statusCd"
                  required
                />
              </v-col>
              <v-col cols="3">
                <TextFieldInput :label="$t('ELE0031')" v-model="searchCondition.partCd" />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="2">
                <TextFieldInput :label="$t('ELE0224')" v-model="searchCondition.model" />
              </v-col>
              <v-col cols="2">
                <PartGroupInput v-model="searchCondition.partGroupCd" />
              </v-col>
              <v-col>
                <CompanyInput v-if="isEpson" :label="$t('ELE0276')" v-model="searchCondition.requestCompanyCd" />
                <PreparedCompanyInput v-else v-model="searchCondition.requestCompanyCd" />
              </v-col>
              <v-col>
                <MemberInput :label="$t('ELE0235')" v-model="searchCondition.requesterCd" />
              </v-col>
              <v-col>
                <MemberInput :label="$t('ELE0279')" v-model="searchCondition.nextCheckerCd" />
              </v-col>
            </v-row>
            <v-row class="pb-0 mb-0">
              <v-col cols="3">
                <SelectSingleInput
                  :items="inspClassCdItems"
                  itemText="name"
                  itemValue="value"
                  :label="$t('ELE0156')"
                  v-model="searchCondition.inspClassCd"
                />
              </v-col>
              <v-col class="pb-0">
                <TextFieldInputWithDatePicker
                  :label="$t('ELE0254')"
                  v-model="searchCondition.endDateFrom"
                  clearable
                  :disabled="searchCondition.saExpiringSoon || searchCondition.validOnly"
                />
              </v-col>
              <span class="px-1 pt-5 pb-0"> {{ $t('ELE0141') }} </span>
              <v-col class="pb-0">
                <TextFieldInputWithDatePicker
                  :label="$t('ELE0254')"
                  v-model="searchCondition.endDateTo"
                  clearable
                  :disabled="searchCondition.saExpiringSoon"
                />
              </v-col>
              <v-col>
                <SelectMultipleInput
                  :items="cmsStatusCdItems"
                  itemText="name"
                  itemValue="value"
                  :label="$t('ELE0510')"
                  v-model="searchCondition.cmsStatusCd"
                />
              </v-col>
            </v-row>
            <v-row class="pt-0 mt-0 mb-1">
              <v-col class="d-flex flex-row py-0">
                <Checkbox
                  :label="$t('ELE0022')"
                  class="mt-0 mr-4"
                  v-model="searchCondition.validOnly"
                  @change="onChangeValidOnly"
                />
                <Checkbox
                  :label="$t('ELE0370')"
                  class="mt-0 mr-4"
                  v-model="searchCondition.saExpiringSoon"
                  @change="onChangeSaExpiringSoon"
                />
                <Checkbox :label="$t('ELE0373')" class="mr-4" v-model="searchCondition.returnedOnly" />
                <Checkbox :label="$t('ELE0516')" class="mr-4" v-model="searchCondition.nearingResponseDate" />
              </v-col>
              <v-col class="pt-0 d-flex justify-end align-center flex-grow-0">
                <Btn
                  class="mr-4"
                  :label="$t('ELE0040')"
                  icon="mdi-download"
                  @click="onClickDownload"
                  :disabled="invalid || !canSearch"
                />
                <BtnSearch @click="onClickSearch" :disabled="invalid || !canSearch" />
              </v-col>
            </v-row>
          </validation-observer>
        </v-card-text>
      </v-card>
      <v-card outlined>
        <v-card-title class="py-0">
          <v-row dense>
            <v-col class="d-flex flex-grow-1">
              {{ $t('ELE0023') }}
            </v-col>
            <v-col class="px-auto pt-0 d-flex justify-end">
              <div class="px-auto pt-0 mb-3 d-flex flex-column">
                <div class="py-1 d-flex">
                  <span class="text-subtitle-2">{{ $t('ELE0517') }}</span>
                  <v-avatar class="mt-1 mx-2" color="#FCF4A1" size="12" />
                  <span class="text-caption">{{ `${$t('ELE0516')} (${enumResponseUntil}${$t('ELE0169')})` }}</span>
                </div>
                <div class="pt-0 d-flex">
                  <span class="text-subtitle-2">{{ $t('ELE0207') }}</span>
                  <v-avatar class="mt-1 mx-2" color="#F8CED2" size="12" />
                  <span class="text-caption">{{ $t('ELE0372') }}</span>
                  <v-avatar class="mt-1 mx-2" color="#FCF4A1" size="12" />
                  <span class="text-caption">{{ `${$t('ELE0371')} (${enumSaExpirationDate}${$t('ELE0169')})` }}</span>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card-title>
        <v-card outlined>
          <v-data-table :headers="headers" :items="items" fixed-header :height="tableHeight" dense>
            <template v-slot:header>
              <thead>
                <tr>
                  <th colspan="19" class="v-data-table__divider"></th>
                  <th colspan="3" class="v-data-table__divider">{{ $t('ELE0233') }}</th>
                  <th colspan="3" class="v-data-table__divider">{{ $t('ELE0278') }}</th>
                </tr>
              </thead>
            </template>
            <template v-slot:item.action="{ item }">
              <IconInquiry @click="onClickInquiry(item.saNo)" />
            </template>
            <template v-slot:item.statusCd="{ item }">
              {{ $t(`SaApprovalStatusCd.${item.statusCd}`) }}
            </template>
            <template v-slot:item.cmsStatusCd="{ item }">
              {{ getEnumlang('CmsApprovalStatusCd.', item.cmsStatusCd) }}
            </template>
            <template v-slot:item.isReturned="{ item }">
              <td align="center" class="v-data-table__divider text-center">
                <v-simple-checkbox v-model="item.isReturned" color="primary" :ripple="false" disabled />
              </td>
            </template>
            <template v-slot:item.inspClassCd="{ item }">
              {{ $t(`InspClassCd.${item.inspClassCd}`) }}
            </template>
            <template v-slot:item.responseUntil="{ item }">
              <td class="v-data-table__divider" :style="{ 'background-color': getResponseUntilCellColor(item) }">
                {{ item.responseUntil }}
              </td>
            </template>
            <template v-slot:item.endDate="{ item }">
              <td class="v-data-table__divider" :style="{ 'background-color': getCellColor(item) }">
                {{ item.endDate }}
              </td>
            </template>
            <!--項目名の先頭単語が短いため、v-datatable標準のalign:rightで右寄せすると、ヘッダーの先頭にソーターが付く-->
            <template v-slot:item.extensionTime="{ item }">
              <td class="text-right v-data-table__divider">{{ item.extensionTime }}</td>
            </template>
          </v-data-table>
        </v-card>
      </v-card>
    </v-container>
    <v-dialog v-if="canRequestSa && saInquiryDialog" v-model="saInquiryDialog" scrollable persistent>
      <v-card>
        <v-card-title class="primary white--text py-2">
          {{ $t('ELE0291') }}
          <v-spacer />
          <Btn icon="mdi-close" @click="saInquiryDialog = false" depressed iconProp color="white" />
        </v-card-title>
        <v-card-text class="px-0">
          <SaRequestForm :propsSaNo="selectedSaNo" @close=";(saInquiryDialog = false), onClickSearch()" isModal />
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-if="!canRequestSa && saInquiryDialog" v-model="saInquiryDialog" scrollable>
      <v-card>
        <v-card-title class="primary white--text py-2">
          {{ $t('ELE0291') }}
          <v-spacer />
          <Btn icon="mdi-close" @click="saInquiryDialog = false" depressed iconProp color="white" />
        </v-card-title>
        <v-card-text class="px-0">
          <SearchSaDetailForm :propsSaNo="selectedSaNo" @close="saInquiryDialog = false" isModal />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
dayjs.extend(isSameOrBefore)

import { API } from '@/plugins/api'
import APIUtils from '@/mixin/api-utils.js'
import Message from '@/mixin/message-utils.js'
import Loading from '@/mixin/loading-utils.js'
import StringUtils from '@/mixin/string-utils.js'
import { ValidationObserver } from 'vee-validate'
import { SaApprovalStatusCd, InspClassCd, SA_EXPIRATION_DATE, RESPONSE_UNTIL, CmsApprovalStatusCd } from '@/enum.js'
import { Storage } from '@/plugins/storage.js'

import Btn from '@/components/atoms/base/button/Btn'
import TextFieldInput from '@/components/atoms/base/text-field/TextFieldInput'
import TextFieldInputWithDatePicker from '@/components/atoms/base/text-field/TextFieldInputWithDatePicker'
import SelectSingleInput from '@/components/atoms/base/select/SelectSingleInput'
import SelectMultipleInput from '@/components/atoms/base/select/SelectMultipleInput'
import Checkbox from '@/components/atoms/base/checkbox/Checkbox'
import BtnSearch from '@/components/atoms/button/BtnSearch'
import IconInquiry from '@/components/atoms/icon/IconInquiry'
import CompanyInput from '@/components/atoms/select/CompanyInput'
import PreparedCompanyInput from '@/components/atoms/select/PreparedCompanyInput'
import MemberInput from '@/components/atoms/select/MemberInput'
import PartGroupInput from '@/components/atoms/select/PartGroupInput'

import SaRequestForm from '@/components/organisms/terminal/form/SaRequestForm'
import SearchSaDetailForm from '@/components/organisms/terminal/form/SearchSaDetailForm'

export default {
  name: 'SearchSaHistoryForm',

  mixins: [APIUtils, Message, Loading, StringUtils],

  components: {
    ValidationObserver,
    Btn,
    TextFieldInput,
    TextFieldInputWithDatePicker,
    SelectSingleInput,
    SelectMultipleInput,
    Checkbox,
    BtnSearch,
    IconInquiry,
    CompanyInput,
    PreparedCompanyInput,
    MemberInput,
    PartGroupInput,
    SaRequestForm,
    SearchSaDetailForm,
  },

  props: {
    propsSaNo: {
      type: String,
      required: false,
      default: '',
    },
    propsPartCd: {
      type: String,
      required: false,
      default: '',
    },
    propsInspClassCd: {
      type: String,
      required: false,
      default: '',
    },
    propsSaExpiringSoon: {
      type: Boolean,
      required: false,
      default: false,
    },
    propsIsWaiting: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      saInquiryDialog: false,
      selectedSaNo: '',
      canRequestSa: false,
      items: [],
      headers: [
        { text: '', value: 'action', divider: true, sortable: false },
        { text: this.$t('ELE0184'), value: 'saNo', divider: true },
        { text: this.$t('ELE0218'), value: 'statusCd', divider: true },
        { text: this.$t('ELE0510'), value: 'cmsStatusCd', divider: true },
        { text: this.$t('ELE0374'), value: 'isReturned', divider: true },
        { text: this.$t('ELE0220'), value: 'currentRouteName', divider: true },
        { text: this.$t('ELE0279'), value: 'currentCheckedBy', divider: true },
        { text: this.$t('ELE0156'), value: 'inspClassCd', divider: true },
        { text: this.$t('ELE0276'), value: 'requestCompanyCd', divider: true },
        { text: this.$t('ELE0004'), value: 'requestCompanyName', divider: true },
        { text: this.$t('ELE0031'), value: 'partCd', divider: true },
        { text: this.$t('ELE0032'), value: 'partName', divider: true },
        { text: this.$t('ELE0224'), value: 'model', divider: true },
        { text: this.$t('ELE0226'), value: 'toolNoCav', divider: true },
        { text: this.$t('ELE0223'), value: 'issueDate', divider: true },
        { text: this.$t('ELE0517'), value: 'responseUntil', divider: true },
        { text: this.$t('ELE0254'), value: 'endDate', divider: true },
        { text: this.$t('ELE0352'), value: 'orgSaNo', divider: true },
        { text: this.$t('ELE0353'), value: 'extensionTime', divider: true },
        { text: this.$t('ELE0235'), value: 'firstPreparedBy', divider: true },
        { text: this.$t('ELE0236'), value: 'firstCheckedBy', divider: true },
        { text: this.$t('ELE0237'), value: 'firstApprovedBy', divider: true },
        { text: this.$t('ELE0277'), value: 'finalRequestBy', divider: true },
        { text: this.$t('ELE0236'), value: 'finalCheckedBy', divider: true },
        { text: this.$t('ELE0237'), value: 'finalApprovedBy' },
      ],
      searchCondition: {
        saNo: '',
        statusCd: [
          SaApprovalStatusCd.TEMP_SAVE,
          SaApprovalStatusCd.SUPPLIER_APPLYING,
          SaApprovalStatusCd.SUPPLIER_APPROVED,
          SaApprovalStatusCd.EPSON_APPLYING,
          SaApprovalStatusCd.APPROVED,
        ],
        cmsStatusCd: [],
        partCd: '',
        partGroupCd: '',
        inspClassCd: '',
        requestCompanyCd: '',
        requesterCd: '',
        nextCheckerCd: '',
        endDateFrom: null,
        endDateTo: null,
        model: '',
        validOnly: false,
        saExpiringSoon: false,
        returnedOnly: false,
        nearingResponseDate: false,
      },
    }
  },

  computed: {
    enumResponseUntil: () => RESPONSE_UNTIL,
    enumSaExpirationDate: () => SA_EXPIRATION_DATE,
    isEpson: {
      get() {
        return this.$store.getters.isEpson
      },
    },
    statusCdItems: {
      get() {
        // プルダウン選択肢をenumから生成
        return Object.values(SaApprovalStatusCd).map((item) => {
          return { name: item + ' : ' + this.$t(`SaApprovalStatusCd.${item}`), value: item }
        })
      },
    },
    cmsStatusCdItems: {
      get() {
        // プルダウン選択肢をenumから生成
        return Object.values(CmsApprovalStatusCd).map((item) => {
          return { name: item + ' : ' + this.$t(`CmsApprovalStatusCd.${item}`), value: item }
        })
      },
    },
    inspClassCdItems: {
      get() {
        // プルダウン選択肢をenumから生成
        return Object.values(InspClassCd).map((item) => {
          return { name: this.$t(`InspClassCd.${item}`), value: item }
        })
      },
    },
    tableHeight: {
      get() {
        let height = this.$vuetify.breakpoint.height * 0.5
        if (height < 360) {
          height = 360
        }
        return `${height}px`
      },
    },
    isFieldAllEmpty: {
      get() {
        return this.isAllEmpty(
          this.searchCondition.saNo,
          this.searchCondition.partCd,
          this.searchCondition.requestCompanyCd,
          this.searchCondition.nextCheckerCd,
          this.searchCondition.endDateFrom,
          this.searchCondition.endDateTo,
          this.searchCondition.nearingResponseDate,
        )
      },
    },
    canSearch: {
      get() {
        let result = false
        if (this.searchCondition.returnedOnly) {
          result = true
        } else {
          result = !this.isFieldAllEmpty
        }
        return result
      },
    },
  },

  methods: {
    getResponseUntilCellColor(item) {
      let result = '#FFFFFF'
      if (
        item.statusCd === SaApprovalStatusCd.TEMP_SAVE ||
        item.statusCd === SaApprovalStatusCd.SUPPLIER_APPLYING ||
        item.statusCd === SaApprovalStatusCd.SUPPLIER_APPROVED ||
        item.statusCd === SaApprovalStatusCd.EPSON_APPLYING
      ) {
        // 通常WFが完了していないものの背景色を変更
        const now = dayjs().startOf('day')
        const responseUntil = dayjs(item.responseUntil)
        if (responseUntil.isSameOrBefore(now.add(RESPONSE_UNTIL, 'd'))) {
          result = '#FCF4A1'
        }
      }
      return result
    },
    getEnumlang(key, value) {
      // langのenumからメッセージ取得
      let lang = ''
      if (value) {
        lang = this.$t(key + value)
      }
      return lang
    },
    getCellColor(item) {
      let result = '#FFFFFF'
      if (!item.isExtended && item.statusCd == SaApprovalStatusCd.APPROVED) {
        const now = dayjs().startOf('day')
        const endDate = dayjs(item.endDate)
        if (endDate.isBefore(now)) {
          result = '#F8CED2'
        } else if (endDate.isSameOrBefore(now.add(SA_EXPIRATION_DATE, 'd'))) {
          result = '#FCF4A1'
        }
      }
      return result
    },
    onChangeValidOnly(validOnly) {
      const now = dayjs()
      if (validOnly) {
        this.searchCondition.endDateFrom = now.format('YYYY-MM-DD')
      }
    },
    onChangeSaExpiringSoon(saExpiringSoon) {
      const now = dayjs()
      if (saExpiringSoon) {
        this.searchCondition.endDateFrom = now.format('YYYY-MM-DD')
        this.searchCondition.endDateTo = now.add(SA_EXPIRATION_DATE, 'd').format('YYYY-MM-DD')
      }
    },
    onClickInquiry(saNo) {
      this.selectedSaNo = saNo
      this.saInquiryDialog = true
    },
    async onClickSearch() {
      try {
        this.startLoading()

        const params = {
          userCompanyCd: this.$store.getters.companyCd,
          saNo: this.searchCondition.saNo,
          statusCd: this.searchCondition.statusCd,
          partCd: this.searchCondition.partCd,
          inspClassCd: this.searchCondition.inspClassCd,
          requestCompanyCd: this.searchCondition.requestCompanyCd,
          requesterCd: this.searchCondition.requesterCd,
          nextCheckerCd: this.searchCondition.nextCheckerCd,
          partGroupCd: this.searchCondition.partGroupCd,
          cmsStatusCd: this.searchCondition.cmsStatusCd,
          endDateFrom: this.searchCondition.endDateFrom,
          endDateTo: this.searchCondition.endDateTo,
          model: this.searchCondition.model,
          saExpiringSoon: this.searchCondition.saExpiringSoon,
          returnedOnly: this.searchCondition.returnedOnly,
          validOnly: this.searchCondition.validOnly,
          responseUntilTo: this.searchCondition.nearingResponseDate
            ? dayjs().startOf('day').add(RESPONSE_UNTIL, 'd').format('YYYY-MM-DD')
            : '',
        }

        this.items = await API.get('/sa', params)
      } catch (error) {
        this.items = []
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0291') })))
      } finally {
        this.stopLoading()
      }
    },
    async onClickDownload() {
      try {
        this.startLoading()

        const params = {
          userCompanyCd: this.$store.getters.companyCd,
          saNo: this.searchCondition.saNo,
          statusCd: this.searchCondition.statusCd,
          partCd: this.searchCondition.partCd,
          inspClassCd: this.searchCondition.inspClassCd,
          requestCompanyCd: this.searchCondition.requestCompanyCd,
          requesterCd: this.searchCondition.requesterCd,
          nextCheckerCd: this.searchCondition.nextCheckerCd,
          partGroupCd: this.searchCondition.partGroupCd,
          cmsStatusCd: this.searchCondition.cmsStatusCd,
          endDateFrom: this.searchCondition.endDateFrom,
          endDateTo: this.searchCondition.endDateTo,
          model: this.searchCondition.model,
          saExpiringSoon: this.searchCondition.saExpiringSoon,
          returnedOnly: this.searchCondition.returnedOnly,
          validOnly: this.searchCondition.validOnly,
          responseUntilTo: this.searchCondition.nearingResponseDate
            ? dayjs().startOf('day').add(RESPONSE_UNTIL, 'd').format('YYYY-MM-DD')
            : '',
        }

        const filePath = await API.get('/sa/file', params)
        Storage.downloadFile(filePath)
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0291') })))
      } finally {
        this.stopLoading()
      }
    },
  },
  created() {
    if (process.env.VUE_APP_CAN_REQUEST_SA) {
      if (process.env.VUE_APP_CAN_REQUEST_SA === 'true') {
        this.canRequestSa = true
      } else if (process.env.VUE_APP_CAN_REQUEST_SA === 'false') {
        this.canRequestSa = false
      }
    }
  },
  mounted() {
    if (this.propsSaExpiringSoon) {
      this.searchCondition.saExpiringSoon = true
      this.onChangeSaExpiringSoon(this.searchCondition.saExpiringSoon)
      this.onClickSearch()
    } else if (this.propsSaNo || this.propsPartCd || this.propsInspClassCd) {
      this.searchCondition.saNo = this.propsSaNo
      this.searchCondition.partCd = this.propsPartCd
      this.searchCondition.inspClassCd = this.propsInspClassCd

      if (this.searchCondition.partCd) {
        this.searchCondition.validOnly = true
        this.onChangeValidOnly(true)
      }
      this.onClickSearch()
    } else if (this.propsIsWaiting) {
      this.searchCondition.nextCheckerCd = this.$store.getters.userCd
      this.onClickSearch()
    }
  },
}
</script>
